import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface LabeledFieldGridItem {
  readonly label: string;
  readonly value: ReactNode;
  /** Use when ``label`` is not unique in the item set. */
  readonly key?: string;
}

export interface LabeledFieldGridProps {
  readonly items: ReadonlyArray<LabeledFieldGridItem>;
  /** Columns at @md+. Defaults to 2. */
  readonly columns?: 1 | 2;
  /** Defaults to ``xs`` for compact detail cards. */
  readonly size?: 'xs' | 'sm';
  readonly className?: string;
  readonly testId?: string;
}

const COLUMN_CLASS: Readonly<Record<1 | 2, string>> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 @md:grid-cols-2',
};

const SIZE_CLASS: Readonly<Record<'xs' | 'sm', string>> = {
  xs: 'gap-x-4 gap-y-1 text-xs',
  sm: 'gap-x-4 gap-y-2 text-sm',
};

/** ``<dl>`` of label/value pairs in a responsive grid. */
export function LabeledFieldGrid({
  items,
  columns = 2,
  size = 'xs',
  className,
  testId,
}: LabeledFieldGridProps) {
  return (
    <dl
      data-testid={testId}
      className={cn('grid', COLUMN_CLASS[columns], SIZE_CLASS[size], className)}
    >
      {items.map((item, index) => (
        <div key={item.key ?? `${item.label}:${index}`} className="flex flex-col">
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="text-foreground">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
